<script lang="ts">
	/**
	 * Main orchestrator for the Dual N-back test.
	 *
	 * Manages the test lifecycle (intro → running → finished) using a simple
	 * phase state machine driven by `$state`.
	 */

	import TestIntro from './TestIntro.svelte';
	import StimulusDisplay from './StimulusDisplay.svelte';
	import ResultsDashboard from './ResultsDashboard.svelte';
	import { createDefaultConfig } from './constants';
	import { generateTrials, evaluateTrial, computeResults } from './engine';
	import { playTone, stopAll } from './audio';
	import type { Trial, TrialResult, TestResults, KeyEvent, VisualStimulus } from './types';

	/**
	 * Phases of the test state machine.
	 * - `intro` : instruction screen shown to the user.
	 * - `running` : trials are being presented.
	 * - `finished` : all trials done, results displayed.
	 */
	type Phase = 'intro' | 'running' | 'finished';

	let phase = $state<Phase>('intro');
	let trials = $state<readonly Trial[]>([]);
	let currentTrialIndex = $state(-1);
	let stimulusVisible = $state(false);
	let currentStimulus: VisualStimulus | null = $state(null);
	let trialResults: TrialResult[] = [];
	let aggregatedResults: TestResults | null = $state(null);

	/** Running trial timer so we can cancel on destroy. */
	let trialTimer: ReturnType<typeof setTimeout> | null = null;

	/** The earliest possible match trial (warm-up). */
	let firstMatchableIndex = 0;

	const config = createDefaultConfig();
	// Pre-generate trials immediately
	trials = generateTrials(config, undefined);
	firstMatchableIndex = config.n;

	// --- Keyboard event tracking ---
	/** All key events captured during the test, keyed by trial index. */
	let allKeyEvents: KeyEvent[] = [];

	/** Timestamp (performance.now) when the current stimulus appeared. */
	let stimulusOnsetTime = 0;

	/** The trial we're currently collecting responses for. */
	let activeTrial: Trial | null = null;

	function handleKeyDown(event: KeyboardEvent) {
		if (phase !== 'running' || activeTrial === null) return;
		const key = event.key.toLowerCase();
		if (key !== config.visualKey && key !== config.auditoryKey) return;

		allKeyEvents.push({
			key,
			timestamp: event.timeStamp,
			trialIndex: activeTrial.index,
		});
	}

	// --- Test lifecycle ---

	function startTest() {
		phase = 'running';
		allKeyEvents = [];
		trialResults = [];
		aggregatedResults = null;
		currentTrialIndex = 0;
		beginTrial(0);
	}

	function beginTrial(index: number) {
		if (index >= trials.length) {
			finishTest();
			return;
		}

		currentTrialIndex = index;
		activeTrial = trials[index];
		currentStimulus = activeTrial.visual;
		stimulusVisible = true;
		stimulusOnsetTime = performance.now();

		// Play the auditory tone
		playTone(activeTrial.auditory, config.stimulusDuration);

		// Schedule stimulus hide
		trialTimer = setTimeout(() => {
			stimulusVisible = false;
			currentStimulus = null;

			// Schedule end of response window + trial evaluation
			trialTimer = setTimeout(() => {
				// Evaluate this trial and store result
				const isWarmup = index < firstMatchableIndex;
				const previous = !isWarmup ? trials[index - config.n] : null;
				const trialKeyEvents = allKeyEvents.filter(
					(e) => e.trialIndex === index,
				);

				const result = evaluateTrial(
					activeTrial!,
					previous,
					trialKeyEvents,
					stimulusOnsetTime,
					config,
				);
				trialResults.push(result);

				// Move to next trial
				beginTrial(index + 1);
			}, config.interStimulusInterval);
		}, config.stimulusDuration);
	}

	function finishTest() {
		phase = 'finished';
		activeTrial = null;
		currentStimulus = null;
		stimulusVisible = false;
		stopAll();
		aggregatedResults = computeResults(trialResults);
	}

	function restartTest() {
		// Cancel any pending timer
		if (trialTimer !== null) {
			clearTimeout(trialTimer);
			trialTimer = null;
		}
		stopAll();
		trials = generateTrials(config, undefined);
		phase = 'intro';
	}

	// --- Lifecycle ---

	$effect(() => {
		// Add keyboard listener when component mounts
		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			if (trialTimer !== null) {
				clearTimeout(trialTimer);
				trialTimer = null;
			}
			stopAll();
		};
	});
</script>

{#if phase === 'intro'}
	<TestIntro onStart={startTest} />
{:else if phase === 'running'}
	<div class="flex flex-col items-center gap-8 py-12">
		<!-- Trial counter -->
		<p class="text-sm text-gray-400">
			시행 {currentTrialIndex + 1} / {trials.length}
		</p>

		<!-- Stimulus area -->
		<StimulusDisplay stimulus={currentStimulus} visible={stimulusVisible} />
	</div>
{:else if phase === 'finished' && aggregatedResults !== null}
	<ResultsDashboard results={aggregatedResults} onRestart={restartTest} />
{/if}
